import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { get, flatten, map, sumBy } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactLoading from 'react-loading';
import axios from 'axios';
import {
  faPlus,
  faEnvelope,
  faStickyNote,
  faEllipsisV,
  faQuestionCircle,
  faCreditCard,
  faTruck,
} from '@fortawesome/free-solid-svg-icons';
import { Button,
  Col,
  Container,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
  Row,
  Table,
  UncontrolledTooltip
} from 'reactstrap';

import {
  getInvoiceDetailStatus,
  preformatFloat,
  locale,
  formatCurrencyForLocale,
  formatDateString,
  allVisibleArePaid
} from '../../../utilities/invoiceUtils';
import { formatChannelText, titleCase } from '../../../utilities/textsFormatters';
import AddChargesModal from '../AddChargesModal';
import AddChargesModalByInvoice from '../AddChargesByInvoiceModal/AddChargesByInvoiceModal';
import { useAxios, useIsComponentMounted } from '../../../hooks';
import Page from '../../Page';
import AddNoteModal from '../AddNoteModal';
import ContactModal from '../ContactModal';
import CalendarModal from '../CalendarModal';
import { cfPath, awsServer } from '../../../utilities/UrlPaths';
import InvaluableShippingBanner from '../InvaluableShippingBanner/InvaluableShippingBanner';
import { selectPolicyID } from '../../../selectors';
import { SHIPPING_METHODS_MAP } from '../constants';

const EditByInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [isOpenInvoiceItemStatusMenu, setIsOpenInvoiceItemStatusMenu] = useState({});
  const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState(null);
  const [isAddChargesByInvoice, setIsAddChargesByInvoice] = useState(false);
  const [isAddChargesModalOpen, setIsAddChargesModalOpen] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [isLiveFeeDisplay, setIsLiveFeeDisplay] = useState(false);
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [isRestrictedModal, setIsRestrictedModal] = useState(false);
  const [invoicesNotes, setInvoicesNotes] = useState({});
  const [calendarID, setCalendarID] = useState(0);
  const [calendarDetailID, setCalendarDetailID] = useState(0);
  const [calendarVariant, setCalendarVariant] = useState('');
  const [calendarCurrentStatus, setCalendarCurrentStatus] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [outstandingBalance, setOutstandingBalance] = useState([]);
  const [calendarPaymentMethod, setCalendarPaymentMethod] = useState(0);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [checkedInvoices, setCheckedInvoices] = useState({});
  const [checkedAllInvoicesIndicator, setCheckAllInvoicesIndicator] = useState(true);
  const [noneSelected, setNoneSelected] = useState(true);
  const [noneEditable, setNoneEditable] = useState(true);
  const [disabledCursorClass, setDisabledCursorClass] = useState('disabled-cursor');
  const [allOrSelected, setAllOrSelected] = useState('All');
  const [topTotals, setTopTotals] = useState({});
  const [isPartialPaid, setIsPartialPaid] = useState({});
  const [bulkIndex, setBulkIndex] = useState(0);
  const [isRestrictedLot, setIsRestrictedLot] = useState(false);
  const [lotVariant, setLotVariant] = useState('lot');
  const [topTotalData, setTopTotalData] = useState({});
  const [pickupShipUpdateObject, setPickupShipUpdateObject] = useState({});
  const { invoiceGroupID } = useParams();
  const history = useHistory();
  const { houseRef, paymentProcessingEnabled, autoChargeEnabled, houseID, houseUserName } = useSelector(state => state.house);
  const { processorType } = useSelector(state => state.invoice);
  const paymentProcessorType = processorType;
  const user = useSelector(state => state.user);
  const isComponentMounted = useIsComponentMounted();
  const queryParams = new URLSearchParams(window.location.search);
  const queryID = queryParams.get('id') || '';
  const blockEl = 'invoices__edit-invoices';
  const balanceArr = [];
  const isInvShippingAvailable = useSelector(selectPolicyID);
  const [shipmentsMap, setShipmentsMap] = useState({});

  const {
    data: groupDetailInfoData,
    exec: execGroupDetailInfo
  } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef],
    { method: 'get' },
    { manual: true }
  );
  const {
    data: editPageData,
    exec: execEditPageData
  } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfoByInvoice`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );

  const { exec: execNotes, isError: isNotesError } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execMessage } = useAxios(
    () => `/app/house/${houseRef}/invoiceMessage`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { data: paymentMethodsData } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/payment-methods`,
    [houseRef]
  );
  const { exec: execPaidDate } = useAxios(
    () => `/app/house/${houseRef}/invoicePaidOnly`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execPaidDateRestricted } = useAxios(
    () => `/app/house/${houseRef}/invoicePaidOnlyByDetail`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execShippedDate } = useAxios(
    () => `/app/house/${houseRef}/invoiceShippedOnlyByDetail`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execAddChargeByInvoice, isSuccess: isAddchargesSuccess } = useAxios(
    () => `/app/house/${houseRef}/additionalCharges`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { execAsync: execAsyncAdditionalCharges } = useAxios(
    () => `/app/house/${houseRef}/additionalCharges`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execPickupShip, data: pickupShipData} = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );

  useEffect(() => {
    if (queryID.length) {
      execEditPageData({
        data: queryID.split(','),
      });
    } else {
      execGroupDetailInfo();
    }
  }, []);

  useEffect(() => {
    if (!(groupDetailInfoData || editPageData)) return;
    const data = groupDetailInfoData?.data?.data?.invoiceBidders || editPageData?.data?.data?.invoiceBidders;
    if (!data) return;
    const controller = new AbortController();
    const fetchShipmentMethod = async (id) => {
      try {
        const response = await axios.get(`/api/shipping/shipment/${id}`, {
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            'x-invaluable-shipping-account-id': `${houseID}`,
            'x-invaluable-shipping-account-domain': 'AUCTIONHOUSE'
          }
        });
        const method = response?.data?.data?.shipment?.arguments?.option?.shippingMethod;
        controller.abort();
        return method;
      } catch (error) {
        controller.abort();
        console.error(error);
      }
    };
    const uniqueShipmentIDs = data.reduce((acc, bidder) => {
      if (bidder.invoiceDetails?.length) {
        bidder.invoiceDetails.forEach((detail) => {
          if (detail.shippingShipmentID) {
            acc.add(detail.shippingShipmentID);
          };
        });
      }
     return acc;
    }, new Set());
    const shipmentsIDsArray = Array.from(uniqueShipmentIDs);
    Promise.all(shipmentsIDsArray.map(async (shipID) => {
      const shipmentMethod = await fetchShipmentMethod(shipID);
      return { shipID, shipmentMethod };
    })).then((results) => {
      let shipmentMethodMap = {};
      results.forEach(({ shipID, shipmentMethod }) => {
        shipmentMethodMap[shipID] = shipmentMethod;
      });
      setShipmentsMap(shipmentMethodMap);
    });

    return () => {
      controller.abort();
    };
  }, [groupDetailInfoData, editPageData]);

  useEffect(() => {
    if (!groupDetailInfoData) return;
    const controller = new AbortController();
    if (isComponentMounted.current) {
      setInvoices(get(groupDetailInfoData, 'data.data.invoiceBidders', []));
      const addlObject = get(groupDetailInfoData, 'data.data.totalPriceWithAdditionalCharges', {});
      const totalObject = get(groupDetailInfoData, 'data.data.totalPrice', {});
      setTopTotalData(addlObject === null ? totalObject : addlObject);
    }
    return () => {
      controller.abort();
    }
  }, [groupDetailInfoData]);

  useEffect(() => {
    if (!editPageData) return;
    if (isComponentMounted.current) {
      setInvoices(get(editPageData, 'data.data.invoiceBidders', []));
      const addlObject = get(editPageData, 'data.data.totalPriceWithAdditionalCharges', {});
      const totalObject = get(editPageData, 'data.data.totalPrice', {});
      setTopTotalData(addlObject === null ? totalObject : addlObject);
    }
  }, [editPageData]);

  useEffect(() => {
    if (typeof paymentMethodsData === 'undefined') {
      return;
    }

    const methods = get(paymentMethodsData, 'data.data.paymentMethods', []);

    setPaymentMethods(methods);
  }, [paymentMethodsData]);

  // refresh the data without reload
  useEffect(() => {
    if (isAddchargesSuccess) {
      if (queryID.length) {
        execEditPageData({
          data: queryID.split(','),
        });
      } else {
        execGroupDetailInfo();
      }
    }
  }, [isAddchargesSuccess]);

  useEffect(() => {
    if (isNotesError) {
      setInvoicesNotes((prev) => {
        const { [`_${currentInvoiceIndex}`]: _, ...restInvoicesNotes } = prev;

        return restInvoicesNotes;
      });
    }
  }, [isNotesError]);

  useEffect(() => {
    if (invoices.length > 0) setCurrencySymbol(invoices[0].totalPrice.currencySymbol);
  }, [invoices]);

  useEffect(() => {
    if (typeof invoices === 'undefined' || invoices.length === 0) {
      return;
    }

    let returnFlag = false;
    const partialPaidObject = {};
    invoices.forEach(invoice => {
      returnFlag = false;
      if (invoice.allPaid) {
        returnFlag = true;
      } else {
        for (let i = 0; i < invoice.invoiceDetails.length; i++) {
          if (invoice.invoiceDetails[i].paid || Number(invoice.invoiceDetails[i].totalAmountPaid) > 0) {
            returnFlag = true;
            break;
          }
        }
      }
      partialPaidObject[invoice.invoiceID] = returnFlag;
    });
    setIsPartialPaid(partialPaidObject);

    const feeValue = Number(invoices[0].invoiceDetails[0].priceInfo.livefee);
    setIsLiveFeeDisplay(feeValue > 0);
  }, [invoices]);

  const canEditInvoiceCharges = ({ allPaid, applyCostPerLot, costsOverride }) => !allPaid && (!applyCostPerLot || !costsOverride);

  const allInvoicesDetails = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return [];
    }

    return flatten(map(invoices, 'invoiceDetails'));
  }, [invoices]);

  const showEditAddChargesToAllButton = useMemo(() => {
    if (typeof invoices === 'undefined' || (Array.isArray(invoices) && !invoices.length)) {
      return false;
    }

    return invoices.some(canEditInvoiceCharges);
  }, [invoices]);

  const invoiceHasRestrictedItems = (id) => {
    const currentInvoice = invoices.find(
      (invoice) => invoice.invoiceID === id
    );
    const { invoiceDetails } = currentInvoice;

    let returnFlag = false;
    invoiceDetails.forEach((item) => {
      if (get(item, 'restricted', false)) {
        returnFlag = true;
      }
    });

    const hasRestrictedDetails = invoiceDetails.find(item => get(item, 'restricted', false)) !== undefined;

    return hasRestrictedDetails;
  };

  const setOutstandinBalance = (outstandingBal) => {
    if (invoices.length >= outstandingBal.length) {
      const pendingTotal = sumBy(
        outstandingBal,
        (invoice) => parseFloat(invoice)
      );
      setOutstandingBalance(pendingTotal.toFixed(2));
    }
  };

  const getOutstandingBalancePerInvoice = useCallback(
    (invoiceId) => {
      const currentInvoice = invoices.find(
        (invoice) => invoice.invoiceID === invoiceId
      );

      if (!currentInvoice) {
        return '0.00';
      }

      const { invoiceDetails, totalPrice } = currentInvoice;

      if (
        typeof invoiceDetails === 'undefined'
      || (Array.isArray(invoiceDetails) && !invoiceDetails.length)
      ) {
        return '0.00';
      }

      let outstandingBal = '0.00';
      if (!currentInvoice.allPaid) {
        outstandingBal = sumBy(
          invoiceDetails,
          (item) => parseFloat(item.paid || item.suppress ? 0 : item.totalAmount - item.priceInfo.tax || 0)
        );
      }

      if (outstandingBal === '0.00') {
        return outstandingBal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      const outstandingBalanceWithAdditionalCharge = outstandingBal + parseFloat(preformatFloat(totalPrice.tax)) + parseFloat(preformatFloat(totalPrice.shipping)) + parseFloat(preformatFloat(totalPrice.insurance)) + parseFloat(preformatFloat(totalPrice.other));
      balanceArr.push(outstandingBalanceWithAdditionalCharge.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      setOutstandinBalance(balanceArr);
      return outstandingBalanceWithAdditionalCharge.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    [invoices]
  );

  const onlyCheckedInvoices = useMemo(
    () => Object.keys(checkedInvoices).reduce((acc, current) => {
      if (checkedInvoices[current]) {
        acc.push(Number(current));
      }

      return acc;
    }, []),
    [checkedInvoices]
  );
  const hasPendingPayment = (id) => {
    const foundInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    const paymentStat = get(foundInvoice, 'onlinePaymentStat', '');
    const isPendingPaymentInvoice = paymentStat === 'p' || paymentStat === 'u' || paymentStat === 'h' || paymentStat === 'i';
    let isPendingPaymentLot = false;
    foundInvoice.invoiceDetails.forEach(lot => {
      const lotStat = get(lot, 'onlinePaymentStat', '');
      if (lotStat === 'p' || lotStat === 'u' || lotStat === 'h' || lotStat === 'i') {
        isPendingPaymentLot = true;
      }
    });

    return isPendingPaymentInvoice || isPendingPaymentLot;
  };

  const canAddPostCharges = (invoice) => {
    let paidByPP = false;
    if (invoice.invoiceDetails.some(item => !item.restricted && (item.paymentMethod.trim() === 'paidByWePay' || item.paymentMethod.trim() === 'ACH'))) {
      paidByPP = true;
    }

    return (invoice.allPaid || allVisibleArePaid(invoice) || invoice.onlinePaymentStat === 'u') && !hasPendingPayment(invoice.invoiceID) && paidByPP && paymentProcessingEnabled && paymentProcessorType === 'p';
  };

  const isPaidByPP = (invoice) => {
    let paidByPP = false;

    if (invoice.invoiceDetails[0].paymentMethod.trim() === 'paidByWePay' || invoice.invoiceDetails[0].paymentMethod.trim() === 'ACH') {
      paidByPP = true;
    }

    return paidByPP;
  };

  const invoicesByLotCharges = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return [];
    }

    // eslint-disable-next-line no-nested-ternary
    // filter removes paid invoices from edit-all modal
    const selectedInvoiceData = invoices.filter(invoice => onlyCheckedInvoices.indexOf(invoice.invoiceID) > -1);
    let filteredInvoicesByIndex = [];
    if (currentInvoiceIndex !== null) {
      filteredInvoicesByIndex = [invoices[currentInvoiceIndex]];
    } else if (onlyCheckedInvoices.length > 0) {
      filteredInvoicesByIndex = selectedInvoiceData.filter(invoice => invoice.allPaid === false || canAddPostCharges(invoice));
      setIsBulkAction(filteredInvoicesByIndex.length > 1);
    } else {
      filteredInvoicesByIndex = invoices.filter(invoice => invoice.allPaid === false || canAddPostCharges(invoice));
    }

    // isAddl flag for sending correct data to modal
    filteredInvoicesByIndex.map(invoice => {
      invoice.isAddl = invoice.invoiceDetails.every(item => item.paid || item.suppress);
    });

    return filteredInvoicesByIndex.map(
      ({ invoiceID,
        firstName,
        lastName,
        invoiceDetails,
        paddleNumber,
        city,
        state,
        province,
        country,
        channel,
        isAddl,
        totalPrice,
      }) => ({
        name: `${lastName}, ${firstName}`,
        id: invoiceID,
        outstandingBalance: getOutstandingBalancePerInvoice(invoiceID),
        paddleNumber,
        location: `${city}, ${state}${province} ${country}`,
        channel,
        country,
        showRestrictedModal: invoiceHasRestrictedItems(invoiceID),
        isPendingPayment: hasPendingPayment(invoiceID),
        totalPrice,
        charges: invoiceDetails.map(
          ({ lotID, lotNum, lotNumExt, priceInfo, suppress, restricted, paid, lotTitle, additionalPriceInfo, paymentMethod, shippingReviewID }) => ({
            suppress,
            restricted,
            lotId: lotID,
            lotDisplayName: `${lotNum}${lotNumExt}`,
            tax: (isAddl ? get(additionalPriceInfo, 'tax', '0.00') : priceInfo.tax) || '0.00',
            shipping: (isAddl ? get(additionalPriceInfo, 'shipping', '0.00') : priceInfo.shipping) || '0.00',
            insurance: (isAddl ? get(additionalPriceInfo, 'insurance', '0.00') : priceInfo.insurance) || '0.00',
            other: (isAddl ? get(additionalPriceInfo, 'other', '0.00') : priceInfo.other) || '0.00',
            additionalTax: get(additionalPriceInfo, 'tax', '0.00'),
            additionalShipping: get(additionalPriceInfo, 'shipping', '0.00'),
            additionalInsurance: get(additionalPriceInfo, 'insurance', '0.00'),
            additionalOther: get(additionalPriceInfo, 'other', '0.00'),
            additionalPaid: get(additionalPriceInfo, 'paid', false),
            additionalPending: (get(additionalPriceInfo, 'onlinePaymentStat', '') === 'u'),
            paid,
            lotTitle,
            hammer: priceInfo.hammer || '0.00',
            premium: priceInfo.premium || '0.00',
            livefee: priceInfo.livefee || '0.00',
            paymentMethod,
            isInvShipping: (shippingReviewID !== ''),
          })
        ),
      })
    );
  }, [invoices, currentInvoiceIndex, onlyCheckedInvoices]);

  const currentInvoiceNotes = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length || typeof invoices[currentInvoiceIndex] === 'undefined') {
      return '';
    }

    return invoicesNotes[`_${currentInvoiceIndex}`] || invoices[currentInvoiceIndex].notes || '';
  }, [invoices, currentInvoiceIndex, invoicesNotes]);

  const modalNameObject = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length || typeof invoices[currentInvoiceIndex] === 'undefined') {
      return '';
    }

    return {
      name: `${invoices[currentInvoiceIndex].lastName}, ${invoices[currentInvoiceIndex].firstName}`,
      paddleNumber: invoices[currentInvoiceIndex].paddleNumber,
      id: invoices[currentInvoiceIndex].invoiceID,
      location: `${invoices[currentInvoiceIndex].city}, ${invoices[currentInvoiceIndex].state}${invoices[currentInvoiceIndex].province} ${invoices[currentInvoiceIndex].country}`,
      channel: invoices[currentInvoiceIndex].channel,
    };
  }, [invoices, currentInvoiceIndex]);

  const handleOnSetCurrentInvoiceIndex = (index) => () => {
    if (index === currentInvoiceIndex) {
      return;
    }

    setCurrentInvoiceIndex(index);
  };

  const handleOnAddNote = (notes) => {
    const currentInvoice = invoices[currentInvoiceIndex];

    if (
      !notes
      || typeof currentInvoice === 'undefined'
      || !currentInvoice.invoiceID
    ) {
      return;
    }

    execNotes({
      data: {
        invoiceNotes: [
          {
            notes,
            invoiceID: currentInvoice.invoiceID,
          },
        ],
      },
    });

    setTimeout(() => {
      const icon = document.getElementById(`noteIcon_${currentInvoice.invoiceID}`);
      icon.classList.add('has-note');
    }, 500);

    setInvoicesNotes((prev) => ({
      ...prev,
      [`_${currentInvoiceIndex}`]: notes
    }));
    setIsAddNoteModalOpen(false);
  };

  const handleOnOpenAddCharges = (invoiceID, isPaid = false) => {
    let thisInvoice = null;
    if (invoiceID !== undefined) {
      thisInvoice = invoices.find(
        (invoice) => invoice.invoiceID === invoiceID
      );
    }

    if (invoiceID === undefined) {
      let editableInvoices = invoices.filter(invoice => !hasPendingPayment(invoice.invoiceID) && (invoice.allPaid === false || canAddPostCharges(invoice)));

      if (onlyCheckedInvoices.length) {
        editableInvoices = editableInvoices.filter(invoice => onlyCheckedInvoices.indexOf(invoice.invoiceID) > -1);
      }
      if (invoiceHasRestrictedItems(editableInvoices[0].invoiceID) && paymentProcessingEnabled) {
        setIsRestrictedModal(true);
        setIsAddChargesModalOpen(true);
        setLotVariant('lot');
      } else {
        setIsRestrictedModal(false);
        setIsAddChargesByInvoice(true);
      }
    } else if (thisInvoice.channel.trim().indexOf('Floor') > -1  && !isPaid) {
      setIsRestrictedModal(true);
      setIsAddChargesModalOpen(true);
      setLotVariant('lot');
    } else if (invoiceHasRestrictedItems(invoiceID) && paymentProcessingEnabled) {
      setIsRestrictedModal(true);
      setIsAddChargesModalOpen(true);
      setLotVariant((isPaid || allVisibleArePaid(thisInvoice)) ? 'lot-additional' : 'lot');
    } else {
      setIsBulkAction(false);
      setIsRestrictedModal(false);
      setIsAddChargesByInvoice(true);
      setLotVariant((isPaid || allVisibleArePaid(thisInvoice)) ? 'lot-additional' : 'lot');
    }
  };

  const handleOnSendMessage = (payload) => {
    const currentInvoice = invoices[currentInvoiceIndex];

    if (
      typeof houseRef === 'undefined'
      || typeof currentInvoice === 'undefined'
      || !currentInvoice.memberID
      || typeof payload !== 'object'
      || !Object.keys(payload).length
    ) {
      return;
    }

    execMessage({
      data: {
        ...payload,
        houseUserRef: user.userRef,
        bidderID: currentInvoice.memberID
      },
    });
    setIsContactModalOpen(false);
  };

  const handleOnOpenInvoiceItemStatusMenu = (index) => () => {
    setIsOpenInvoiceItemStatusMenu((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const showColumn = (columnName, id) => {
    let returnFlag = false;
    let currentValue = 0;
    let addlValue = 0;
    invoices.forEach((currentInvoice) => {
      if (currentInvoice.invoiceID === id) {
        // not restricted
        currentValue = currentInvoice.totalPrice[columnName] || '0.00';
        addlValue = currentInvoice.additionalPriceInfo !== undefined && currentInvoice.additionalPriceInfo !== null ? currentInvoice.additionalPriceInfo[columnName] : '0.00';
        if (currentValue !== '0.00' || addlValue !== '0.00') {
          returnFlag = true;
        }
        // restricted
        if (!returnFlag) {
          currentInvoice.invoiceDetails.forEach((item) => {
            if (item.restricted === true) {
              currentValue = item.priceInfo[columnName] || '0.00';
              addlValue = item.additionalPriceInfo !== undefined && item.additionalPriceInfo !== null ? item.additionalPriceInfo[columnName] : '0.00';
              if (currentValue !== '0.00' || addlValue !== '0.00') {
                returnFlag = true;
              }
            }
          });
        }
      }
    });
    return returnFlag;
  };

  const showDisputesColumn = (id) => {
    let returnFlag = false;
    invoices.forEach((currentInvoice) => {
      if (currentInvoice.invoiceID === id) {
        currentInvoice.invoiceDetails.forEach((item) => {
          if (item.disputeRef) {
            returnFlag = true;
          }
        });
      }
    });
    return returnFlag;
  };

  const openCalendar = (e) => {
    const id = Number(e.currentTarget.attributes.invoiceid.value);
    setCalendarID(id);
    const detailID = Number(get(e.currentTarget, 'attributes.detailid.value', 0));
    setCalendarDetailID(detailID);
    setCalendarVariant(e.currentTarget.attributes.variant.value);
    setCalendarDate(e.currentTarget.attributes.date.value);
    setCalendarPaymentMethod(e.currentTarget.attributes.paymentmethodid.value || 0);
    setIsRestrictedLot(invoiceHasRestrictedItems(id));
    // Current Status is true if this invoice is paid or shipped (based on variant)
    setCalendarCurrentStatus(e.currentTarget.attributes.currentstatus.value === 'true');
    setIsCalendarModalOpen(true);
  };

  const handleSetShippedDate = (postData) => {
    execShippedDate({
      data: {
        paidInvoices: [postData],
      }
    });

    const invoicesCopy = invoices;
    invoicesCopy.forEach((currentInvoice) => {
      if (currentInvoice.invoiceID === postData.invoiceID) {
        currentInvoice.invoiceDetails.forEach((item) => {
          if (item.invoiceDetailID === postData.invoiceDetailID) {
            item.shipped = true;
            item.shippedDateString = postData.shippedDate;
          }
        });
      }
    });
    setInvoices(invoicesCopy);

    setIsCalendarModalOpen(false);
  };

  const handleSetPaidDate = (postData) => {
    if (isRestrictedLot) {
      execPaidDateRestricted({
        data: {
          paidInvoices: [postData],
        }
      });
    } else {
      execPaidDate({
        data: {
          paidInvoices: [postData],
        }
      });
    }

    const invoicesCopy = invoices;
    invoicesCopy.forEach((currentInvoice) => {
      if (currentInvoice.invoiceID === postData.invoiceID) {
        if (!postData.paid) currentInvoice.allPaid = false;
        //  mark the items too
        const { invoiceDetails } = currentInvoice;
        if (postData.invoiceDetailID) {
          const currentLot = invoiceDetails.find(
            (lot) => lot.invoiceDetailID === postData.invoiceDetailID
          );
          currentLot.paid = postData.paid;
          currentLot.paidDateString = postData.paid ? postData.paidDate : null;
        } else {
          currentInvoice.allPaid = postData.paid;
          invoiceDetails.forEach((item) => {
            item.paid = postData.paid;
            item.paidDateString = postData.paid ? postData.paidDate : null;
          });
        }
      }
    });
    setInvoices(invoicesCopy);

    setIsCalendarModalOpen(false);
  };

  const hideShowItem = async (e) => {
    const type = e.currentTarget.id.substring(0, 4);
    const invoiceDetailID = e.currentTarget.id.substring(5);
    const invoiceID = e.currentTarget.attributes.invoice.value;
    const suppress = (type === 'hide');

    await execNotes({
      data: {
        invoiceDetails: [
          {
            invoiceID,
            invoiceDetailID,
            suppress,
          },
        ],
        invoiceDetailPrice: [
          {
            invoiceID,
            invoiceDetailID,
            suppress,
          },
        ],
      },
    });
    if (!isNotesError) {
      const invoicesCopy = invoices;
      invoicesCopy.forEach((invoice) => {
        if (invoice.invoiceID === Number(invoiceID)) {
          invoice.invoiceDetails.forEach((detail) => {
            if (detail.invoiceDetailID === Number(invoiceDetailID)) {
              detail.suppress = suppress;
              detail.priceInfo.suppress = suppress;
            }
          });
        }
      });
      setInvoices(invoicesCopy);
    }
  };

  const isUsPayrixUser = (id) => {
    const currentInvoice = invoices.find(
      (invoice) => invoice.invoiceID === id
    );
    return (paymentProcessorType === 'p' && currentInvoice.country === 'US');
  };

  const handleOnSaveInvoiceCharges = async (invoice, isAddl = false, floorByInvoiceArray = []) => {
    const isFloorBidder = invoice.channel.trim().indexOf('Floor') > -1 && !invoice.allPaid;
    // floor bidder updates
    if (isFloorBidder) {
      const currentInvoice = invoices.find(
        (myInvoice) => myInvoice.invoiceID === invoice.id
      );
      const { invoiceDetails } = currentInvoice;
      const updatedFloorCharges = [];

      invoice.charges.forEach(lot => {
        // get original lot data
        const currentLot = invoiceDetails.find(
          (myDetail) => myDetail.lotID === lot.lotId
        );
        // floor charges
        if (currentLot.priceInfo.hammer !== lot.hammer
          || currentLot.priceInfo.premium !== lot.premium
          || currentLot.priceInfo.livefee !== lot.livefee) {
            updatedFloorCharges.push(lot)
          }
      });

      if (updatedFloorCharges.length > 0) {
        //  build the payload
        const lotObjects = [];
        updatedFloorCharges.map(lot => {
          const thisLot = {
            lotId: lot.lotId,
            hammer: parseFloat(preformatFloat(lot.hammer)),
            buyersPremium: parseFloat(preformatFloat(lot.premium)),
            liveFee: parseFloat(preformatFloat(lot.livefee)),
          };
          lotObjects.push(thisLot)
        });

        const data = {
          type: 'invoice',
          id: invoice.id,
          attributes: {
            invoiceDetails: lotObjects,
          }
        }

        const postBody = { data };
        const postHeaders = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          channel: 'infinitebidding',
          houseusername: houseUserName,
          Authorization: `${houseRef}*`
          }
        };

        await axios.patch(`/app/house/${houseRef}/invoice/${invoice.id}`, JSON.stringify(postBody), postHeaders);
      }
      // invoice level charges for floor bidder
      if (floorByInvoiceArray.length > 0) {
        await execAddChargeByInvoice({
          data: floorByInvoiceArray[0],
        });
      }
    } else {
      if (isAddl) {
        await Promise.all(
          invoice.charges.map(
            ({ updated, lotDisplayName, lotId, suppress, restricted, additionalTax, additionalShipping, additionalInsurance, additionalOther, ...restCharges }) => {
              if (updated) {
                return execAsyncAdditionalCharges({
              data: {
                suppress,
                restricted,
                ...(!isUsPayrixUser(invoice.id) && { tax: additionalTax }),
                shipping: additionalShipping,
                insurance: additionalInsurance,
                other: additionalOther,
                invoiceID: invoice.id,
                lotID: lotId,
                additionalCharge: true,
              },
              headers: { Authorization: `${houseRef}*` }
            })
              }
            }
          )
        );
      } else {
        await Promise.all(
          invoice.charges.map(
            ({ updated, lotDisplayName, lotId, isInvShipping, ...restCharges }) => {
              if (updated) {
                return execAsyncAdditionalCharges({
                  data: {
                    ...restCharges,
                    invoiceID: invoice.id,
                    lotID: lotId,
                  },
                  headers: { Authorization: `${houseRef}*` }
                })
              }
              return null;
            }
          )
        );
      }
    }
    // not sure why this works
    setBulkIndex(bulkIndex + 1);
  };

  const handleAddChargesByInvoice = async (additionalCharges) => {
    let thisInvoice = null;
    let allPaid = false;
    if (additionalCharges.data.invoiceID !== undefined) {
      thisInvoice = invoices.find(
        (invoice) => invoice.invoiceID === additionalCharges.data.invoiceID
      );
      allPaid = thisInvoice.allPid || allVisibleArePaid(thisInvoice);
    }

    const dataObject = {
      shipping: Number(additionalCharges.data.shipping),
      other: Number(additionalCharges.data.other),
      insurance: Number(additionalCharges.data.insurance),
      invoiceID: Number(additionalCharges.data.invoiceID),
      additionalCharge: (allPaid || (lotVariant === 'lot-additional')),
    };
    if (!isUsPayrixUser(additionalCharges.data.invoiceID)) {
      dataObject.tax = Number(additionalCharges.data.tax);
    }

    await execAddChargeByInvoice({
      data: dataObject,
    });

    // not sure why this works
    if (additionalCharges.data.isLastInvoice) {
      setIsAddChargesByInvoice(false);
    } else {
      setBulkIndex(bulkIndex + 1);
    }
  };

  const handleBulkActions = (invoiceCount) => {
    if (!invoiceCount) {
      setCurrentInvoiceIndex(null);
      setIsBulkAction(false);
    }
  };

  const handleUpdateInvoice = (updatedInvoice) => {
    setInvoices((prevInvoices) =>
      prevInvoices.map((invoice) => {
        if (invoice.invoiceID === updatedInvoice.id) {
          const updatedDetails = invoice.invoiceDetails.map((detail) => {
            const updatedCharge = updatedInvoice.charges.find(
              (charge) => charge.lotId === detail.lotID
            );

            if (updatedCharge && updatedCharge.updated) {
              const updatedPriceInfo = {
                ...detail.priceInfo,
                hammer: updatedCharge.hammer ?? detail.priceInfo.hammer,
                premium: updatedCharge.premium ?? detail.priceInfo.premium,
                livefee: updatedCharge.livefee ?? detail.priceInfo.livefee,
                tax: updatedCharge.tax ?? detail.priceInfo.tax,
                shipping: updatedCharge.shipping ?? detail.priceInfo.shipping,
                insurance: updatedCharge.insurance ?? detail.priceInfo.insurance,
                other: updatedCharge.other ?? detail.priceInfo.other,
              };

              const updatedAdditionalPriceInfo = {
                ...detail.additionalPriceInfo,
                tax: updatedCharge.additionalTax ?? detail.additionalPriceInfo?.tax,
                shipping: updatedCharge.additionalShipping ?? detail.additionalPriceInfo?.shipping,
                insurance: updatedCharge.additionalInsurance ?? detail.additionalPriceInfo?.insurance,
                other: updatedCharge.additionalOther ?? detail.additionalPriceInfo?.other,
              };

              // Recalculate totalAmount
              const hammer = parseFloat(preformatFloat(updatedPriceInfo.hammer)) || 0;
              const premium = parseFloat(preformatFloat(updatedPriceInfo.premium)) || 0;
              const livefee = parseFloat(preformatFloat(updatedPriceInfo.livefee)) || 0;
              const tax = parseFloat(preformatFloat(updatedPriceInfo.tax)) || 0;
              const shipping = parseFloat(preformatFloat(updatedPriceInfo.shipping)) || 0;
              const insurance = parseFloat(preformatFloat(updatedPriceInfo.insurance)) || 0;
              const other = parseFloat(preformatFloat(updatedPriceInfo.other)) || 0;

              const additionalTax = parseFloat(preformatFloat(updatedAdditionalPriceInfo.tax)) || 0;
              const additionalShipping = parseFloat(preformatFloat(updatedAdditionalPriceInfo.shipping)) || 0;
              const additionalInsurance = parseFloat(preformatFloat(updatedAdditionalPriceInfo.insurance)) || 0;
              const additionalOther = parseFloat(preformatFloat(updatedAdditionalPriceInfo.other)) || 0;

              const newTotalAmount = hammer + premium + livefee + tax + shipping + insurance + other +
                                     additionalTax + additionalShipping + additionalInsurance + additionalOther;

              return {
                ...detail,
                priceInfo: updatedPriceInfo,
                additionalPriceInfo: updatedAdditionalPriceInfo,
                totalAmount: newTotalAmount.toFixed(2),
              };
            }

            return detail;
          });

          const sumPriceInfo = (field) => updatedDetails.reduce(
            (acc, d) => acc + (parseFloat(preformatFloat(get(d, `priceInfo.${field}`, '0'))) || 0),
            0,
          ).toFixed(2);

          const updatedTotalPrice = {
            ...invoice.totalPrice,
            tax: sumPriceInfo('tax'),
            shipping: sumPriceInfo('shipping'),
            insurance: sumPriceInfo('insurance'),
            other: sumPriceInfo('other'),
          };

          return {
            ...invoice,
            invoiceDetails: updatedDetails,
            totalPrice: updatedTotalPrice,
          };
        }

        return invoice;
      })
    );
  };

  useEffect(() => {
    if (!onlyCheckedInvoices.length) {
      setCheckAllInvoicesIndicator(true);
    }
    setNoneSelected(onlyCheckedInvoices.length === 0);
    // none editable accounts for payment pending and hides edit link
    let editableInvoices = invoices.filter(invoice => !hasPendingPayment(invoice.invoiceID) && (invoice.allPaid === false || canAddPostCharges(invoice)));
    if (onlyCheckedInvoices.length) {
      editableInvoices = editableInvoices.filter(invoice => onlyCheckedInvoices.indexOf(invoice.invoiceID) > -1);
    }
    setNoneEditable(editableInvoices.length === 0);

    setDisabledCursorClass(onlyCheckedInvoices.length === 0 ? 'disabled-cursor' : '');

    if (onlyCheckedInvoices.length === invoices.length || onlyCheckedInvoices.length === 0) {
      setAllOrSelected('All');
    } else {
      setAllOrSelected('Selected');
    }
  }, [onlyCheckedInvoices]);

  const handleOnCheckedAllInvoices = (markAsChecked) => () => {
    const checkedAllInvoices = invoices.reduce((acc, current) => {
      acc[current.invoiceID] = markAsChecked;

      return acc;
    }, {});

    setCheckedInvoices(checkedAllInvoices);
    setCheckAllInvoicesIndicator(!markAsChecked);
    setCurrentInvoiceIndex(null);
  };

  const handleOnChangeInvoiceChecked = (invoiceId) => () => {
    setCheckedInvoices((prev) => ({
      ...prev,
      [invoiceId]: !prev[invoiceId],
    }));
  };

  const handleOnPreviewSend = () => {
    if (!invoiceGroupID || !onlyCheckedInvoices.length) {
      return;
    }
    window.sessionStorage.setItem('fromCreateInvoices', 'false');
    if (paymentProcessorType === 'p') {
      window.sessionStorage.setItem('autoChargeSource', 'edit');
    } else {
      window.sessionStorage.removeItem('autoChargeSource');
    }
    const previewRedirectPage = autoChargeEnabled && paymentProcessorType === 'p' ? 'InvoiceAutoCharge' : 'InvoiceGroupPreview';
    history.push(
      `/invoices/${previewRedirectPage}/${invoiceGroupID}/invoice/${onlyCheckedInvoices.join(
        ','
      )}`,
      { replace: true }
    );
  };

  const invoicesCharges = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return [];
    }

    // eslint-disable-next-line no-nested-ternary
    // filter removes paid invoices from edit-all modal
    const selectedInvoiceData = invoices.filter(invoice => onlyCheckedInvoices.indexOf(invoice.invoiceID) > -1);
    let filteredInvoices = [];
    if (currentInvoiceIndex !== null) {
      filteredInvoices = [invoices[currentInvoiceIndex]];
    } else if (onlyCheckedInvoices.length > 0) {
      filteredInvoices = selectedInvoiceData.filter(invoice => invoice.allPaid === false || canAddPostCharges(invoice));
      setIsBulkAction(filteredInvoices.length > 1);
    } else {
      filteredInvoices = invoices.filter(invoice => invoice.allPaid === false || canAddPostCharges(invoice));
    }

    return filteredInvoices.map(
      ({ invoiceID,
        firstName,
        lastName,
        totalPrice,
        paddleNumber,
        city,
        state,
        province,
        country,
        channel,
        invoiceDetails,
        allPaid,
        additionalPriceInfo,
      }) => ({
        name: `${lastName}, ${firstName}`,
        id: invoiceID,
        outstandingBalance: getOutstandingBalancePerInvoice(invoiceID),
        paddleNumber,
        location: `${city}, ${state}${province} ${country}`,
        channel,
        country,
        showRestrictedModal: invoiceHasRestrictedItems(invoiceID),
        isPendingPayment: hasPendingPayment(invoiceID),
        isPaid: allPaid,
        invoiceDetails,
        allPaid,
        charges: {
          tax: (invoiceDetails.every(item => item.paid || item.suppress || hasPendingPayment(invoiceID)) ? get(additionalPriceInfo, 'tax', '0.00') : totalPrice.tax) || '0.00',
          shipping: (invoiceDetails.every(item => item.paid || item.suppress || hasPendingPayment(invoiceID)) ? get(additionalPriceInfo, 'shipping', '0.00') : totalPrice.shipping) || '0.00',
          insurance: (invoiceDetails.every(item => item.paid || item.suppress || hasPendingPayment(invoiceID)) ? get(additionalPriceInfo, 'insurance', '0.00') : totalPrice.insurance) || '0.00',
          other: (invoiceDetails.every(item => item.paid || item.suppress || hasPendingPayment(invoiceID)) ? get(additionalPriceInfo, 'other', '0.00') : totalPrice.other) || '0.00',
        },
        paidCharges: {
          tax: totalPrice.tax || '0.00',
          shipping: totalPrice.shipping || '0.00',
          insurance: totalPrice.insurance || '0.00',
          other: totalPrice.other || '0.00',
        },
        additionalPaid: get(additionalPriceInfo, 'paid', false),
        additionalPending: (get(additionalPriceInfo, 'onlinePaymentStat', '') === 'u'),
        isInvShipping: invoiceDetails.every(item => item.shippingReviewID !== ''),
      })
    );
  }, [invoices, currentInvoiceIndex, isBulkAction, onlyCheckedInvoices]);

  const showPaddle = (paddle) => (paddle !== 0 && paddle !== '');
  const isSuperUser = user.isSuperUser !== null;

  useEffect(() => {
    if (typeof allInvoicesDetails === 'undefined') {
      return;
    }

    const getUnsuppressedCount = () => {
      let rawCount = 0;
      allInvoicesDetails.forEach(item => {
        if (!item.suppress) {
          rawCount++;
        }
      });
      return rawCount;
    };
    const getGrandTotal = () => {
      const rawValue = sumBy(
        invoices,
        (invoice) => parseFloat(preformatFloat(invoice.invoiceTotal))
      );

      return rawValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const topTotalObject = {
      invoiceTotal: getGrandTotal(),
      itemCount: getUnsuppressedCount(),
    };

    setTopTotals(topTotalObject);
  }, [allInvoicesDetails, invoices]);

  const additionalChargeTotal = (id) => {
    const currentInvoice = invoices.find(
      (invoice) => invoice.invoiceID === id
    );
    const currentAdditionalTotal = parseFloat(preformatFloat(currentInvoice.totalPrice.insurance)) + parseFloat(preformatFloat(currentInvoice.totalPrice.other)) + parseFloat(preformatFloat(currentInvoice.totalPrice.shipping)) + parseFloat(preformatFloat(currentInvoice.totalPrice.tax));

    return currentAdditionalTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const additionalChargeTotalPostPay = (id) => {
    const currentInvoice = invoices.find(
      (invoice) => invoice.invoiceID === id
    );
    const currentAdditionalTotal = parseFloat(preformatFloat(get(currentInvoice, 'additionalPriceInfo.insurance', 0))) + parseFloat(preformatFloat(get(currentInvoice, 'additionalPriceInfo.other', 0))) + parseFloat(preformatFloat(get(currentInvoice, 'additionalPriceInfo.shipping', 0))) + parseFloat(preformatFloat(get(currentInvoice, 'additionalPriceInfo.tax', 0)));
    return currentAdditionalTotal;
  };

  const additionalChargeTotalPostPayByLot = (id, column = '') => {
    const currentInvoice = invoices.find(
      (invoice) => invoice.invoiceID === id
    );
    let grandTotal = 0;
    let itemTotal = 0;
    currentInvoice.invoiceDetails.forEach(detail => {
      if (column) {
        itemTotal = parseFloat(preformatFloat(detail.additionalPriceInfo[column] || 0));
      } else {
        itemTotal = parseFloat(preformatFloat(get(detail, 'additionalPriceInfo.insurance', 0))) + parseFloat(preformatFloat(get(detail, 'additionalPriceInfo.other', 0))) + parseFloat(preformatFloat(get(detail, 'additionalPriceInfo.shipping', 0))) + parseFloat(preformatFloat(get(detail, 'additionalPriceInfo.tax', 0)));
      }
      grandTotal += itemTotal;
    });

    return grandTotal;
  };

  const additionalChargeTotalPerLot = (lotID) => {
    let theTotal = 0;
    invoices.forEach(invoice => {
      invoice.invoiceDetails.forEach(detail => {
        if (detail.lotID === lotID) {
          theTotal = parseFloat(preformatFloat(detail.additionalPriceInfo.insurance)) + parseFloat(preformatFloat(detail.additionalPriceInfo.other)) + parseFloat(preformatFloat(detail.additionalPriceInfo.shipping)) + parseFloat(preformatFloat(detail.additionalPriceInfo.tax));
        }
      });
    });

    return theTotal;
  };

  const checkForModalSwap = (variant, swap = 0) => {
    if (variant === 'lot') {
      setIsAddChargesModalOpen(false);
      if (swap) {
        setIsRestrictedModal(false);
        setIsAddChargesByInvoice(true);
      }
    } else {
      setIsAddChargesByInvoice(false);
      if (swap) {
        setIsRestrictedModal(true);
        setIsAddChargesModalOpen(true);
      }
    }
  };

  const additionalChargesPaid = (invoice) => {
    let chargesPaid = false;
    if (invoiceHasRestrictedItems(invoice.invoiceID)) {
      if (invoice.invoiceDetails.some(item => item.additionalPriceInfo !== undefined && item.additionalPriceInfo !== null && item.additionalPriceInfo.paid)) {
        chargesPaid = true;
      }
    } else if (invoice.additionalPriceInfo !== undefined && invoice.additionalPriceInfo !== null && invoice.additionalPriceInfo.paid) {
      chargesPaid = true;
    }

    return chargesPaid;
  };

  const hasAdditionalCharges = (id) => {
    const currentInvoice = invoices.find(
      (invoice) => invoice.invoiceID === id
    );
    let returnFlag = false;
    if (currentInvoice.additionalPriceInfo !== null) {
      returnFlag = true;
    } else {
      currentInvoice.invoiceDetails.forEach(detail => {
        if (detail.additionalCharge) {
          returnFlag = true;
        }
      });
    }

    return returnFlag;
  };

  const isFullyUnpaid = (id) => {
    const currentInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    let returnFlag = true;

    if (currentInvoice.allPaid) {
      returnFlag = false;
    } else if (hasPendingPayment(currentInvoice.invoiceID) ) {
      returnFlag = false;
    } else {
      for (let i = 0; i < currentInvoice.invoiceDetails.length; i++) {
        if (currentInvoice.invoiceDetails[i].paid || Number(currentInvoice.invoiceDetails[i].totalAmountPaid) > 0) {
          returnFlag = false;
          break;
        }
      }
    }

    return returnFlag;
  };

  const processPickupShip = async (e) => {
    // show spinner
    const list = document.getElementById(`bubbles_${e.currentTarget.id}`).classList;
    list.remove('d-none');
    // the object is used to update the screen in the useEffect below
    const linkLabel = document.getElementById(`shippingLink_${e.currentTarget.id}`);
    const newText = e.currentTarget.attributes.changestate.value === 'shipping' ? 'pickup' : 'shipping';
    const changeElement = e.currentTarget.attributes.changestate;
    const updateObject = {
      linkLabel,
      changeElement,
      newText,
      id: e.currentTarget.id,
    };
    setPickupShipUpdateObject(updateObject);

    const postData = {
      deliveryMode: e.currentTarget.attributes.changestate.value,
      invoiceID: e.currentTarget.id,
    };

    try {
      await execPickupShip({
          data: {
            invoiceItemDeliveryModes: [postData]
          }
        });
    } catch (err) {
      console.log('processPickupShip post failure', err);
    }
  };

  useEffect(() => {
    if (get(pickupShipData, 'data.data.success', false)) {
      pickupShipUpdateObject.linkLabel.innerText = titleCase(pickupShipUpdateObject.newText);
      pickupShipUpdateObject.changeElement.value = pickupShipUpdateObject.newText;

      if (queryID.length) {
        execEditPageData({
          data: queryID.split(','),
        });
      } else {
        execGroupDetailInfo();
      }
    }
  }, [pickupShipData]);

  useEffect(() => {
    const collection = document.getElementsByClassName('bubble-spinner');
    if (collection.length > 0) {
      for (let i = 0; i < collection.length; i++) {
        collection[i].classList.add('d-none')
      };
    }
  }, [invoices]);

  const formatShippingMethod = (shipID) => {
    const method = shipmentsMap[shipID] || '';
    if (method) {
      if (method === 'invaluable_shipping') {
        return (
          <>
            <img src={`${awsServer}/static/invShippingIcon.png`} alt="shipping truck" className="shippingTruckIcon height-20" />
            <span className="pl-2">
              Inv Ship
            </span>
          </>
        );
      } else {
        return SHIPPING_METHODS_MAP[method];
      }
    } else {
      return (
        <span className="italic-text">Not Selected</span>
      )
    }
  };

  if (invoices.length === 0) {
    return (
      <ReactLoading className="loading-spinner" type="spin" color="black" />
    );
  }

  return (
    <>
      <Page
        className={blockEl}
        header={{ title: '' }}
        navLink={{
          displayName: 'Back to Invoice Group Details',
          href: `/invoices/InvoiceGroupDetails/${invoiceGroupID}`,
        }}
      >
        <Container>
          {!!isInvShippingAvailable && (
            <div className="mt-n3 mb-4">
              <InvaluableShippingBanner />
            </div>
          )}
          <Row className={`${blockEl}__header`}>
            <Col xs={12} className={`${blockEl}__header-title`}>
              <h1>
                Edit Invoices
              </h1>
            </Col>
            <Col xs={12} className={`${blockEl}__totals pl-0`}>
              <div className="totals-table my-3 w-100">
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Invoices
                  </div>
                  <div>
                    {invoices.length}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Items
                  </div>
                  <div>
                    {topTotals.itemCount}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Hammer
                  </div>
                  <div>
                    {currencySymbol} {topTotalData.hammer}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total BP
                  </div>
                  <div>
                    {currencySymbol}{topTotalData.premium}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Live Fee
                  </div>
                  <div>
                    {currencySymbol}{topTotalData.livefee}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Tax
                  </div>
                  <div>
                    {currencySymbol}{topTotalData.tax}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total S/H
                  </div>
                  <div>
                    {currencySymbol}{topTotalData.shipping}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Insurance
                  </div>
                  <div>
                    {currencySymbol}{topTotalData.insurance}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Total Other
                  </div>
                  <div>
                    {currencySymbol}{topTotalData.other}
                  </div>
                </div>
                <div className="totals-table-cell">
                  <div className="totals-table-head">
                    Invoice Total
                  </div>
                  <div>
                    {currencySymbol}{topTotals.invoiceTotal}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={6} className="ml-n3">
              {checkedAllInvoicesIndicator ? (
                <Button color="link" onClick={handleOnCheckedAllInvoices(true)}>
                  Select All Invoices
                </Button>
              ) : (
                <Button
                  color="link"
                  onClick={handleOnCheckedAllInvoices(false)}
                >
                  Deselect all
                </Button>
              )}
              {!noneEditable && (
              <Button
                color="link"
                className="ml-4"
                onClick={() => {
                  setCurrentInvoiceIndex(null);
                  setIsBulkAction(true);
                  handleOnOpenAddCharges();
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Edit/Add Charges to {allOrSelected} {paymentProcessorType !== 'p' && '(unpaid)'}
              </Button>
              )}
            </Col>
            <Col className={`${blockEl}__header-actions`}>
              <Button
                color="primary"
                onClick={handleOnPreviewSend}
                disabled={noneSelected}
                className={disabledCursorClass}
              >
                Preview &amp; Send {allOrSelected}
              </Button>
            </Col>
          </Row>
          {invoices.length
          && invoices.map((invoice, index) => (
            <Row className={`${blockEl}__invoice`} id={invoice.invoiceID} key={invoice.invoiceID}>
              <Col xs={12} style={{ paddingBottom: 0 }}>
                <Row className={`${blockEl}__invoice-details`}>
                  <Col xs={6}>

                    <Row>
                      <Col xs="auto" className={`${blockEl}__invoice-checkbox`}>
                        <Input
                          type="checkbox"
                          key={Math.random()}
                          onClick={handleOnChangeInvoiceChecked(invoice.invoiceID)}
                          defaultChecked={checkedInvoices[invoice.invoiceID]}
                        />
                      </Col>
                      <Col>

                        <div className={`${blockEl}__invoice-details__name`}>
                          <h3>
                            <a href={`${cfPath()}/al/bidders/viewBidder.cfm?approvalID=${invoice.approvalID}`} target="_blank" rel="noopener noreferrer">
                              {invoice.lastName}, {invoice.firstName}
                            </a>
                            {showPaddle(invoice.paddleNumber) && (
                            <span className="small ml-1">
                              (Paddle #{invoice.paddleNumber})
                            </span>
                            )}
                          </h3>
                        </div>
                        <div className="mb-2">
                          {invoice.email}
                        </div>
                        <div>
                          Invoice #{invoice.invoiceID} | {invoice.city},{' '}
                          {invoice.state || invoice.province},{' '} {invoice.country}
                          {' '}| {formatChannelText(invoice.channel)}
                        </div>
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={6} className={`${blockEl}__invoice-balance`}>
                    <div className="d-flex justify-content-end gap-4">
                      <div className="text-end">
                        <div className="fw-bold mb-1">Invoice Total</div>
                        <div>{currencySymbol} {formatCurrencyForLocale(invoice.invoiceTotal)}</div>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold mb-1">Payments Made</div>
                        <div>{currencySymbol} {formatCurrencyForLocale(invoice.invoicePaid)}</div>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold mb-1">Outstanding Balance</div>
                        <div>{currencySymbol} {formatCurrencyForLocale(invoice.outStandingBalance)}</div>
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col
                    xs={12}
                    className={`${blockEl}__invoice-details__actions`}
                    onClick={handleOnSetCurrentInvoiceIndex(index)}
                  >
                    {showEditAddChargesToAllButton && (!invoice.allPaid || !allVisibleArePaid(invoice)) && !hasPendingPayment(invoice.invoiceID) && (invoice.onlinePaymentStat !== 'a' || invoiceHasRestrictedItems(invoice.invoiceID)) && invoice.onlinePaymentStat !== 'u' && (
                    <div className="float-left ml-4">
                      <Button
                        color="link"
                        onClick={() => {
                          setIsBulkAction(true);
                          handleOnOpenAddCharges(invoice.invoiceID);
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        Edit/Add charges
                      </Button>
                    </div>
                    )}
                    {canAddPostCharges(invoice) && !additionalChargesPaid(invoice) && !hasPendingPayment(invoice.invoiceID) && (
                      <div className="float-left ml-4">
                        <Button
                          color="link"
                          onClick={() => {
                            setIsBulkAction(true);
                            handleOnOpenAddCharges(invoice.invoiceID, true);
                          }}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                          Edit/Add charges
                        </Button>
                      </div>
                    )}
                    <div className="float-left ml-5">
                      <Button
                        color="link"
                        onClick={() => {
                          setIsContactModalOpen(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faEnvelope} />
                        Contact
                      </Button>
                    </div>
                    <div className="float-left ml-5">
                      <Button
                        color="link"
                        onClick={() => {
                          setIsAddNoteModalOpen(true);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faStickyNote}
                          className={invoice.notes !== '' ? 'has-note' : ''}
                          id={`noteIcon_${invoice.invoiceID}`}
                        />
                        {invoice.notes !== '' ? 'Update' : 'Add'} internal note
                      </Button>
                    </div>
                    {!hasPendingPayment(invoice.invoiceID) && (invoice.onlinePaymentStat !== 'a') && !invoiceHasRestrictedItems(invoice.invoiceID) && !isPaidByPP(invoice) && (
                      <div className="float-left ml-5">
                        <Button
                          color="link"
                          invoiceid={invoice.invoiceID}
                          detailid={0}
                          variant="Paid"
                          date=""
                          paymentmethodid={0}
                          onClick={(e) => { openCalendar(e); }}
                          currentstatus={invoice.allPaid.toString()}
                        >
                          <FontAwesomeIcon icon={faCreditCard} />
                          Mark as {invoice.allPaid && 'Not '}Paid
                        </Button>
                      </div>
                    )}
                    {isFullyUnpaid(invoice.invoiceID) && (
                      <>
                        <div className="float-left ml-5">
                          < Button
                            color="link"
                            id={invoice.invoiceID}
                            changestate={invoice.pickup === undefined || !invoice.pickup ? 'pickup' : 'shipping'}
                            onClick={(e) => { processPickupShip(e); }}
                          >
                            <FontAwesomeIcon icon={faTruck} />
                            Mark as <span id={`shippingLink_${invoice.invoiceID}`}>{titleCase(invoice.pickup === undefined || !invoice.pickup ? 'pickup' : 'shipping')}</span>
                          </Button>
                        </div>
                        <div id={`bubbles_${invoice.invoiceID}`} className="float-left bubble-spinner d-none">
                          <ReactLoading
                            className="mt-1"
                            type="bubbles"
                            color="black"
                            height={'45px'}
                            width={'45px'}
                          />
                        </div>
                      </>
                    )}
                  </Col>
                </Row>

                {paymentProcessingEnabled && invoiceHasRestrictedItems(invoice.invoiceID) && (
                <div className="alert alert-warning m2-3" role="alert">
                  <span className="alert-content">
                    <FontAwesomeIcon icon="credit-card" className="" />
                    <span className="pl-2">
                      One or more item(s) in this invoice are restricted and cannot be paid for online. Additional charges will need to be added on an item level.
                    </span>
                  </span>
                </div>
                )}
              </Col>
              <Col xs="12">
                <Row>
                  <Table
                    bordered
                    className={`${blockEl}__invoice__amounts edit-table`}
                  >
                    <thead>
                      <tr>
                        <th>Session</th>
                        <th>Lot</th>
                        <th>Hammer</th>
                        <th>BP</th>
                        {isLiveFeeDisplay && (
                          <th>Live Fee</th>
                        )}
                        {showColumn('tax', invoice.invoiceID) && (
                        <th>Tax</th>
                        )}
                        {showColumn('shipping', invoice.invoiceID) && (
                        <th>S/H</th>
                        )}
                        {showColumn('insurance', invoice.invoiceID) && (
                        <th>Insurance</th>
                        )}
                        {showColumn('other', invoice.invoiceID) && (
                        <th>
                          Other {' '}
                          <FontAwesomeIcon
                            icon={faQuestionCircle}
                            id="otherTooltip"
                            style={{ cursor: 'pointer' }}
                          />
                          <UncontrolledTooltip placement="right" target="otherTooltip">
                            This column is for adding or discounting any additional fees.
                          </UncontrolledTooltip>
                        </th>
                        )}
                        <th>Total</th>
                        {showDisputesColumn(invoice.invoiceID) && (
                          <th>Disputes</th>
                        )}
                        <th>Invoice Status</th>
                        {!!isInvShippingAvailable && (
                          <th>Shipping Status</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {(invoice.invoiceDetails && invoice.invoiceDetails.length)
                        && invoice.invoiceDetails.map((invoiceDetail) => (
                          <tr
                            key={`${invoiceDetail.invoiceDetailID}_fee`}
                            className={`${blockEl}__invoice__item ${(invoiceDetail.suppress || invoiceDetail.priceInfo.suppress) ? `${blockEl}__invoice__hidden-item` : ''}`}
                          >
                            <td className="edit-cell-session">{invoiceDetail.saleIdentifier}</td>
                            <td id={`lotnum_${invoiceDetail.lotRef}`} className="edit-cell-small">
                              <Button color="link">
                                <a
                                  href={invoiceDetail.lotURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`${(invoiceDetail.suppress || invoiceDetail.priceInfo.suppress) ? `${blockEl}__invoice__hidden-item` : ''}`}
                                >
                                  {invoiceDetail.lotNum}
                                  {invoiceDetail.lotNumExt}
                                </a>
                              </Button>
                              {paymentProcessingEnabled && invoiceDetail.restricted && ('*')}
                              <UncontrolledTooltip placement="right" target={`lotnum_${invoiceDetail.lotRef}`}>
                                {invoiceDetail.lotTitle}
                              </UncontrolledTooltip>
                            </td>
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">
                                  {formatCurrencyForLocale(invoiceDetail.priceInfo.hammer)}
                                </Col>
                              </Row>
                            </td>
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">
                                  {formatCurrencyForLocale(invoiceDetail.priceInfo.premium)}
                                </Col>
                              </Row>
                            </td>
                            {isLiveFeeDisplay
                              && (
                              <td className="edit-cell-med">
                                <Row>
                                  <Col xs={1}>{currencySymbol}</Col>
                                  <Col className="text-right">
                                    {formatCurrencyForLocale(invoiceDetail.priceInfo.livefee)}
                                  </Col>
                                </Row>
                              </td>
                              )}
                            {showColumn('tax', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              {invoiceHasRestrictedItems(invoice.invoiceID) && paymentProcessingEnabled && (
                                <Row>
                                  <Col xs={1}>{currencySymbol}</Col>
                                  <Col className="text-right">{formatCurrencyForLocale(invoiceDetail.priceInfo.tax)}</Col>
                                </Row>
                              )}
                            </td>
                            )}
                            {showColumn('shipping', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              {invoiceHasRestrictedItems(invoice.invoiceID) && paymentProcessingEnabled && (
                                <Row>
                                  <Col xs={1}>{currencySymbol}</Col>
                                  <Col className="text-right">{formatCurrencyForLocale(invoiceDetail.priceInfo.shipping)}</Col>
                                </Row>
                              )}
                            </td>
                            )}
                            {showColumn('insurance', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              {invoiceHasRestrictedItems(invoice.invoiceID) && paymentProcessingEnabled && (
                                <Row>
                                  <Col xs={1}>{currencySymbol}</Col>
                                  <Col className="text-right">{formatCurrencyForLocale(invoiceDetail.priceInfo.insurance)}</Col>
                                </Row>
                              )}
                            </td>
                            )}
                            {showColumn('other', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              {invoiceHasRestrictedItems(invoice.invoiceID) && paymentProcessingEnabled && (
                                <Row>
                                  <Col xs={1}>{currencySymbol}</Col>
                                  <Col className="text-right">{formatCurrencyForLocale(invoiceDetail.priceInfo.other)}</Col>
                                </Row>
                              )}
                            </td>
                            )}
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">
                                  {formatCurrencyForLocale(invoiceDetail.totalAmount)}
                                </Col>
                              </Row>
                            </td>
                            {showDisputesColumn(invoice.invoiceID) && (
                              <td className="edit-cell-med">
                                {invoiceDetail.disputeRef && (
                                  <Button color="link">
                                    <a href={`/ah/customers/dispute/${invoiceDetail.disputeRef}`} target="_blank" rel="noopener noreferrer">
                                      View
                                    </a>
                                  </Button>
                                )}
                              </td>
                            )}
                            <td>
                              <Table borderless size="sm" className="status-table margin-top-0">
                                <tbody>
                                  <tr>
                                    <td className={invoiceDetail.suppress ? 'invoices__edit-invoices__invoice__hidden-item' : ''}>
                                      {getInvoiceDetailStatus(
                                        invoice.invoiceSentDateString,
                                        invoiceDetail.paidDateString,
                                        invoiceDetail.onlinePaymentStat,
                                        invoiceDetail.paid,
                                        invoiceDetail.suppress
                                      )}
                                      {invoiceDetail.paid && invoiceDetail.paymentMethod.trim() === 'paidByWePay' && (
                                      <span className="ml-3">
                                        <FontAwesomeIcon icon={faCreditCard} className="fa-lg" />
                                      </span>
                                      )}
                                      {!invoiceDetail.shipped && (
                                      <Dropdown isOpen={isOpenInvoiceItemStatusMenu[invoiceDetail.invoiceDetailID] || false} direction="left" toggle={handleOnOpenInvoiceItemStatusMenu(invoiceDetail.invoiceDetailID)}>
                                        <DropdownToggle>
                                          <FontAwesomeIcon icon={faEllipsisV} />
                                        </DropdownToggle>
                                        <DropdownMenu className={`${blockEl}__invoice__dropdown-link`}>
                                          {!invoiceDetail.shipped && (
                                          <DropdownItem
                                            invoiceid={invoice.invoiceID}
                                            detailid={invoiceDetail.invoiceDetailID}
                                            date={invoiceDetail.shippedDateString}
                                            paymentmethodid={0}
                                            variant="Shipped"
                                            onClick={(e) => { openCalendar(e); }}
                                            currentstatus={invoiceDetail.shipped.toString()}
                                          >
                                            Mark as shipped
                                          </DropdownItem>
                                          )}
                                          {invoiceHasRestrictedItems(invoice.invoiceID) && paymentProcessingEnabled && !(invoiceDetail.paid && (invoiceDetail.paymentMethod.trim() === 'paidByWePay' || invoiceDetail.paymentMethod.trim() === 'ACH')) && !allVisibleArePaid(invoice) && (
                                            <DropdownItem
                                              invoiceid={invoice.invoiceID}
                                              detailid={invoiceDetail.invoiceDetailID}
                                              date={invoiceDetail.paidDateString}
                                              paymentmethodid={invoiceDetail.paymentMethodID}
                                              variant="Paid"
                                              onClick={(e) => { openCalendar(e); }}
                                              currentstatus={invoiceDetail.paid.toString()}
                                            >
                                              Mark as {invoiceDetail.paid && 'not '}paid
                                            </DropdownItem>
                                          )}
                                          {invoice.allPaid && invoiceDetail.paymentMethod.trim() !== 'paidByWePay' && invoiceDetail.paymentMethod.trim() !== 'ACH' && !invoiceHasRestrictedItems(invoice.invoiceID) && (
                                          <DropdownItem
                                            invoiceid={invoice.invoiceID}
                                            detailid={0}
                                            date={invoiceDetail.paidDateString}
                                            paymentmethodid={invoiceDetail.paymentMethodID}
                                            variant="Paid"
                                            onClick={(e) => { openCalendar(e); }}
                                            currentstatus={invoiceDetail.paid.toString()}
                                          >
                                            Mark invoice as not paid
                                          </DropdownItem>
                                          )}
                                          {!invoiceDetail.paid && invoiceDetail.onlinePaymentStat !== 'a' && !hasPendingPayment(invoice.invoiceID) && !allVisibleArePaid(invoice) && (
                                            (invoiceDetail.suppress || invoiceDetail.priceInfo.suppress) ? (
                                              <DropdownItem
                                                id={`show_${invoiceDetail.invoiceDetailID}`}
                                                invoice={invoice.invoiceID}
                                                onClick={(e) => { hideShowItem(e); }}
                                              >
                                                Show on invoice
                                              </DropdownItem>
                                            ) : (
                                              <DropdownItem
                                                id={`hide_${invoiceDetail.invoiceDetailID}`}
                                                invoice={invoice.invoiceID}
                                                onClick={(e) => { hideShowItem(e); }}
                                              >
                                                Hide from invoice
                                              </DropdownItem>
                                            )
                                          )}
                                          {(invoiceDetail.canOpenDispute || isSuperUser) && !invoiceDetail.disputeRef && invoice.channel !== 'Floor Bidders' && (
                                          <DropdownItem onClick={() => { window.open(`/ah/reports/openDisputes/${invoiceDetail.lotRef}`, '_blank'); }}>
                                            File as dispute
                                          </DropdownItem>
                                          )}
                                        </DropdownMenu>
                                      </Dropdown>
                                      )}
                                    </td>
                                  </tr>
                                  {invoiceDetail.shipped && (
                                  <tr>
                                    <td>
                                      Shipped {invoiceDetail.shippedDateString && (
                                      ` ${formatDateString(invoiceDetail.shippedDateString)}`
                                    )}
                                    </td>
                                  </tr>
                                  )}
                                </tbody>
                              </Table>
                            </td>
                            {!!isInvShippingAvailable && (
                              <td className="border-bottom-0">
                                {formatShippingMethod(invoiceDetail.shippingShipmentID)}
                              </td>
                            )}
                          </tr>
                        ))}

                      {invoice.invoiceDetails && invoice.invoiceDetails.length && (
                      <>
                        <tr style={{ fontSize: '2px' }}>
                          <td colSpan={isLiveFeeDisplay ? 12 : 11}>&nbsp;</td>
                        </tr>
                        <tr className="fees-row">
                          <td className="edit-cell-total-tax" colSpan={isLiveFeeDisplay ? 5 : 4}>
                            Total Taxes and Fees
                          </td>
                          {showColumn('tax', invoice.invoiceID) && (
                          <td className="edit-cell-med">
                            <Row>
                              <Col xs={1}>{currencySymbol}</Col>
                              <Col className="text-right">{formatCurrencyForLocale(invoice.totalPrice.tax)}</Col>
                            </Row>
                          </td>
                          )}
                          {showColumn('shipping', invoice.invoiceID) && (
                          <td className="edit-cell-med">
                            <Row>
                              <Col xs={1}>{currencySymbol}</Col>
                              <Col className="text-right">{formatCurrencyForLocale(invoice.totalPrice.shipping)}</Col>
                            </Row>
                          </td>
                          )}
                          {showColumn('insurance', invoice.invoiceID) && (
                          <td className="edit-cell-med">
                            <Row>
                              <Col xs={1}>{currencySymbol}</Col>
                              <Col className="text-right">{formatCurrencyForLocale(invoice.totalPrice.insurance)}</Col>
                            </Row>
                          </td>
                          )}
                          {showColumn('other', invoice.invoiceID) && (
                          <td className="edit-cell-med">
                            <Row>
                              <Col xs={1}>{currencySymbol}</Col>
                              <Col className="text-right">{formatCurrencyForLocale(invoice.totalPrice.other)}</Col>
                            </Row>
                          </td>
                          )}
                          <td className="edit-cell-med">
                            <Row>
                              <Col xs={1}>{currencySymbol}</Col>
                              <Col className="text-right">{additionalChargeTotal(invoice.invoiceID)}</Col>
                            </Row>
                          </td>
                          {showDisputesColumn(invoice.invoiceID) && (
                            <td className="edit-cell-med">&nbsp;</td>
                          )}
                          <td className="edit-cell-med">
                            {getInvoiceDetailStatus(
                              invoice.invoiceSentDateString,
                              invoice.invoiceDetails[0].paidDateString,
                              invoice.onlinePaymentStat,
                              allVisibleArePaid(invoice),
                            )}
                            {invoice.allPaid && invoice.invoiceDetails[0].paymentMethod.trim() === 'paidByWePay' && (
                              <span className="ml-3">
                                <FontAwesomeIcon icon={faCreditCard} className="fa-lg" />
                              </span>
                            )}
                          </td>
                          {!!isInvShippingAvailable && (
                            <td>&nbsp;</td>
                          )}
                        </tr>
                      </>
                      )}

                      {canAddPostCharges(invoice) && !invoiceHasRestrictedItems(invoice.invoiceID) && (
                        <>
                          <tr className="fees-row">
                            <td className="edit-cell-total-tax" colSpan={isLiveFeeDisplay ? 5 : 4}>
                              Total Additional Taxes and Fees
                            </td>
                            {showColumn('tax', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">{formatCurrencyForLocale(get(invoice, 'additionalPriceInfo.tax', 0))}</Col>
                              </Row>
                            </td>
                            )}
                            {showColumn('shipping', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">{formatCurrencyForLocale(get(invoice, 'additionalPriceInfo.shipping', 0))}</Col>
                              </Row>
                            </td>
                            )}
                            {showColumn('insurance', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">{formatCurrencyForLocale(get(invoice, 'additionalPriceInfo.insurance', 0))}</Col>
                              </Row>
                            </td>
                            )}
                            {showColumn('other', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">{formatCurrencyForLocale(get(invoice, 'additionalPriceInfo.other', 0))}</Col>
                              </Row>
                            </td>
                            )}
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">{formatCurrencyForLocale(additionalChargeTotalPostPay(invoice.invoiceID))}</Col>
                              </Row>
                            </td>
                            {showDisputesColumn(invoice.invoiceID) && (
                            <td className="edit-cell-med">&nbsp;</td>
                            )}
                            <td className="edit-cell-med">
                              {invoice.allPaid && (additionalChargeTotalPostPay(invoice.invoiceID) > 0) && (
                              <span>
                                {getInvoiceDetailStatus(
                                  get(invoice, 'additionalPriceInfo.additionalChargeSentDateString', ''),
                                  get(invoice, 'additionalPriceInfo.paidDateString', ''),
                                  get(invoice, 'additionalPriceInfo.onlinePaymentStat', ''),
                                  get(invoice, 'additionalPriceInfo.paid', false),
                                )}
                                {get(invoice, 'additionalPriceInfo.paid', false) && get(invoice, 'invoiceDetails[0].paymentMethod', '').trim() === 'paidByWePay' && (
                                  <span className="ml-3">
                                    <FontAwesomeIcon icon={faCreditCard} className="fa-lg" />
                                  </span>
                                )}
                              </span>
                              )}
                            &nbsp;
                            </td>
                            {!!isInvShippingAvailable && (
                              <td>&nbsp;</td>
                            )}
                          </tr>
                        </>
                      )}

                      {canAddPostCharges(invoice) && invoiceHasRestrictedItems(invoice.invoiceID) && hasAdditionalCharges(invoice.invoiceID) && (
                        <>
                          <tr style={{ fontSize: '2px' }}>
                            <td colSpan={isLiveFeeDisplay ? 12 : 11}>&nbsp;</td>
                          </tr>
                          <tr className="fees-row">
                            <td className="edit-cell-total-tax" colSpan={isLiveFeeDisplay ? 5 : 4}>
                              Additional Taxes and Fees
                            </td>
                            {showColumn('tax', invoice.invoiceID) && (
                            <td className="font-weight-bold">Tax</td>
                            )}
                            {showColumn('shipping', invoice.invoiceID) && (
                            <td className="font-weight-bold">S/H</td>
                            )}
                            {showColumn('insurance', invoice.invoiceID) && (
                            <td className="font-weight-bold">Insurance</td>
                            )}
                            {showColumn('other', invoice.invoiceID) && (
                            <td className="font-weight-bold">Other</td>
                            )}
                            <td className="font-weight-bold">Total</td>
                            <td className="font-weight-bold">Status</td>
                          </tr>
                          {(invoice.invoiceDetails && invoice.invoiceDetails.length)
                            && invoice.invoiceDetails.map((detail) => (
                              <tr key={`${detail.invoiceDetailID}_addl`}>
                                <td className="edit-cell-session">{detail.saleIdentifier}</td>
                                <td className="edit-cell-small">
                                  {detail.lotNum}
                                  {detail.lotNumExt}
                                  {paymentProcessingEnabled && detail.restricted && ('*')}
                                </td>
                                <td colSpan={isLiveFeeDisplay ? 3 : 2} />
                                {showColumn('tax', invoice.invoiceID) && (
                                <td className="edit-cell-med">
                                  <Row>
                                    <Col xs={1}>{currencySymbol}</Col>
                                    <Col className="text-right">{formatCurrencyForLocale(get(detail, 'additionalPriceInfo.tax', 0))}</Col>
                                  </Row>
                                </td>
                                )}
                                {showColumn('shipping', invoice.invoiceID) && (
                                <td className="edit-cell-med">
                                  <Row>
                                    <Col xs={1}>{currencySymbol}</Col>
                                    <Col className="text-right">{formatCurrencyForLocale(get(detail, 'additionalPriceInfo.shipping', 0))}</Col>
                                  </Row>
                                </td>
                                )}
                                {showColumn('insurance', invoice.invoiceID) && (
                                <td className="edit-cell-med">
                                  <Row>
                                    <Col xs={1}>{currencySymbol}</Col>
                                    <Col className="text-right">{formatCurrencyForLocale(get(detail, 'additionalPriceInfo.insurance', 0))}</Col>
                                  </Row>
                                </td>
                                )}
                                {showColumn('other', invoice.invoiceID) && (
                                <td className="edit-cell-med">
                                  <Row>
                                    <Col xs={1}>{currencySymbol}</Col>
                                    <Col className="text-right">{formatCurrencyForLocale(get(detail, 'additionalPriceInfo.other', 0))}</Col>
                                  </Row>
                                </td>
                                )}
                                <td className="edit-cell-med">
                                  <Row>
                                    <Col xs={1}>{currencySymbol}</Col>
                                    <Col className="text-right">
                                      {formatCurrencyForLocale(additionalChargeTotalPerLot(detail.lotID))}
                                    </Col>
                                  </Row>
                                </td>
                                <td>
                                  {getInvoiceDetailStatus(
                                    detail.additionalPriceInfo.additionalChargeSentDateString,
                                    detail.additionalPriceInfo.paidDateString,
                                    detail.additionalPriceInfo.onlinePaymentStat,
                                    detail.additionalPriceInfo.paid,
                                    detail.suppress
                                  )}
                                </td>
                                {!!isInvShippingAvailable && (
                                  <td>&nbsp;</td>
                                )}
                              </tr>
                            ))}
                          <tr className="fees-row">
                            <td className="edit-cell-total-tax" colSpan={isLiveFeeDisplay ? 5 : 4}>
                              Total Additional Taxes and Fees
                            </td>
                            {showColumn('tax', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">{formatCurrencyForLocale(additionalChargeTotalPostPayByLot(invoice.invoiceID, 'tax'))}</Col>
                              </Row>
                            </td>
                            )}
                            {showColumn('shipping', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">{formatCurrencyForLocale(additionalChargeTotalPostPayByLot(invoice.invoiceID, 'shipping'))}</Col>
                              </Row>
                            </td>
                            )}
                            {showColumn('insurance', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">{formatCurrencyForLocale(additionalChargeTotalPostPayByLot(invoice.invoiceID, 'insurance'))}</Col>
                              </Row>
                            </td>
                            )}
                            {showColumn('other', invoice.invoiceID) && (
                            <td className="edit-cell-med">
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">{formatCurrencyForLocale(additionalChargeTotalPostPayByLot(invoice.invoiceID, 'other'))}</Col>
                              </Row>
                            </td>
                            )}
                            <td>
                              <Row>
                                <Col xs={1}>{currencySymbol}</Col>
                                <Col className="text-right">{formatCurrencyForLocale(additionalChargeTotalPostPayByLot(invoice.invoiceID))}</Col>
                              </Row>
                            </td>
                            <td>&nbsp;</td>
                            <td />
                          </tr>
                        </>
                      )}
                    </tbody>
                  </Table>
                  {paymentProcessingEnabled && invoiceHasRestrictedItems(invoice.invoiceID) && (
                    <div className="ml-2 mt-3">
                      *this item is restricted and cannot be paid for online
                    </div>
                  )}
                </Row>
              </Col>
            </Row>
          ))}
        </Container>
      </Page>
      {isRestrictedModal ? (
        <AddChargesModal
          isOpen={isAddChargesModalOpen}
          invoices={invoicesByLotCharges}
          variant={lotVariant}
          onClose={(variant, swap, index) => {
            setBulkIndex(index);
            checkForModalSwap(variant, swap);
          }}
          currencySymbol={currencySymbol}
          onSave={handleOnSaveInvoiceCharges}
          onUpdateInvoice={handleUpdateInvoice}
          setIndex={bulkIndex}
          setIsAddChargesModalOpen={setIsAddChargesModalOpen}
          paymentProcessorType={paymentProcessorType}
          hideAdditionalCode={false}
        />
      ) : (
        <>
          {isAddChargesByInvoice && (
            <AddChargesModalByInvoice
              isBulkAction={isBulkAction}
              isOpen={isAddChargesByInvoice}
              invoices={invoicesCharges}
              onClose={(variant, swap, index) => {
                setBulkIndex(index);
                checkForModalSwap(variant, swap);
              }}
              currencySymbol={currencySymbol}
              handleAddChargesByInvoice={handleAddChargesByInvoice}
              handleBulkActions={handleBulkActions}
              paymentProcessorType={paymentProcessorType}
              setIndex={bulkIndex}
              variant={lotVariant}
              hideAdditionalCode={false}
            />
          )}
        </>
      )}
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        notes={currentInvoiceNotes}
        modalNameObject={modalNameObject}
        onClose={() => {
          setIsAddNoteModalOpen(false);
        }}
        onSave={handleOnAddNote}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        modalNameObject={modalNameObject}
        onClose={() => {
          setIsContactModalOpen(false);
        }}
        onSave={handleOnSendMessage}
      />
      <CalendarModal
        isOpen={isCalendarModalOpen}
        invoiceID={calendarID}
        invoiceDetailID={calendarDetailID}
        variant={calendarVariant}
        paymentMethods={paymentMethods}
        paymentMethodID={calendarPaymentMethod}
        presetDate={calendarDate}
        onClose={() => {
          setIsCalendarModalOpen(false);
        }}
        onSave={(calendarVariant === 'Shipped') ? handleSetShippedDate : handleSetPaidDate}
        currentStatus={calendarCurrentStatus}
      />
    </>
  );
};

export default EditByInvoices;
